<template>
  <template-component :key="route.fullPath" />
</template>

<script lang="ts" setup>
import { withTrailingSlash } from 'ufo';
import { navigateTo, setPageLayout } from '#app';
import { computed, onMounted } from 'vue';
import { definePageMeta, useAsyncData, useRoute } from '#imports';
import { usePagesStore } from '~/stores/pages';
import TemplateComponent from '~/components/templates/TemplateComponent.vue';
import { useLivePreview } from '~/composables/live-preview';
import { DEFAULT_LAYOUT } from '~/constants/layout';
import { useEntryPasswordProtected } from '~/composables/entry-password-protected';

const route = useRoute();
const pages = usePagesStore();
const { checkEntry, redirect } = useEntryPasswordProtected();
const { getLivePreviewHashFromPath, handleLivePreviewMode } = useLivePreview();
const livePreviewHash = getLivePreviewHashFromPath(route);

const { data: page } = await useAsyncData(
  route.path,
  async () => {
    return pages.getPage(route.path);
  },
  {
    watch: [route],
  }
);

if (!page.value) {
  const { data: isProtectedPage } = await useAsyncData(
    `path-protected-${route.path}`,
    async () => {
      return checkEntry(route.path, 'pages');
    },
    {
      watch: [route],
    }
  );

  if (isProtectedPage.value) {
    await redirect('pages', livePreviewHash);
  }
}

pages.setPage(route.path, page.value);

onMounted(() => {
  handleLivePreviewMode(livePreviewHash);
});

if (pages.currentPage && pages.currentPage.redirect) {
  navigateTo(withTrailingSlash(pages.currentPage.redirect), { external: true });
}

const pageLayout = computed(() => {
  const blueprint = page.value?.blueprint?.handle;
  return blueprint === 'iframe_page' ? 'iframe-page' : DEFAULT_LAYOUT;
});

setPageLayout(pageLayout.value);

definePageMeta({
  key: (route) => route.fullPath,
  pageType: 'page',
  layout: DEFAULT_LAYOUT,
});
</script>
